import { useMemo, useState } from 'react';
import { Plus, Pencil, Ban, Play, Copy, Trash2, ChevronDown, ChevronRight, BadgePercent, SearchX } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/data-table';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  usePromotions,
  useCreatePromotion,
  useUpdatePromotion,
  useDeletePromotion,
  useSetPromotionActive,
} from '@/hooks/use-promotions';
import type { Promotion, PromotionInput, PromotionType } from '@/hooks/use-promotions';
import { useLocations } from '@/hooks/use-locations';
import { useDepartments } from '@/hooks/use-departments';
import { useCategories } from '@/hooks/use-categories';
import { useProducts } from '@/hooks/use-products';
import { useLocationStore } from '@/stores/location-store';
import { usePermissions } from '@/hooks/use-permissions';
import { toast } from '@/stores/toast-store';
import { formatDateTime, formatCurrency } from '@/lib/utils';
import { PromotionForm } from './promotion-form';
import { isWarehouseType } from '@/lib/location-type';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const TYPE_LABEL: Record<PromotionType, string> = {
  buy_x_get_y_free: 'Buy X Get Y Free',
  tiered_pricing: 'Tiered Pricing',
  discounted_price: 'Discounted Price',
  buy_x_get_discount: 'Buy X Get Discount',
};

function buildNameMap(rows: any[] | undefined, idKey = 'id', nameKey = 'name'): Map<string, string> {
  const map = new Map<string, string>();
  for (const r of rows ?? []) map.set(r[idKey], r[nameKey]);
  return map;
}

// Open-ended bounds: a null startsAt has always begun, a null endsAt never
// expires. Returns NaN-safe booleans for the running/scheduled/expired buckets.
function isScheduled(p: Promotion, now: number): boolean {
  return !!p.startsAt && now < new Date(p.startsAt).getTime();
}
function isExpired(p: Promotion, now: number): boolean {
  return !!p.endsAt && now > new Date(p.endsAt).getTime();
}
function isRunning(p: Promotion, now: number): boolean {
  return !isScheduled(p, now) && !isExpired(p, now);
}

function statusLabel(p: Promotion): { label: string; tone: string } {
  if (!p.isActive) return { label: 'Deactivated', tone: 'bg-gray-100 text-gray-700' };
  const now = Date.now();
  if (isScheduled(p, now)) return { label: 'Scheduled', tone: 'bg-amber-100 text-amber-800' };
  if (isExpired(p, now)) return { label: 'Expired', tone: 'bg-gray-100 text-gray-600' };
  return { label: 'Running', tone: 'bg-green-100 text-green-800' };
}

type StatusFilter = 'all' | 'running' | 'scheduled' | 'expired' | 'deactivated';

export function PromotionListPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // Stopped (deactivated) promotions stay in the list so they can be started
  // again; only deleted ones are gone. The server never returns deleted rows.
  const { data, isLoading } = usePromotions(true);
  // Restrict the table to promotions that actually apply at the currently
  // selected location. A promotion with no locations attached is treated as
  // "all locations" and is always shown. Cashiers + managers on a specific
  // store shouldn't see other-store promos cluttering the list.
  const { selectedLocationId } = useLocationStore();
  // Promotions are store-only — warehouses never run POS checkouts and
  // can't be on a promotion's scope. If the global header dropdown is
  // pointed at a warehouse we render an empty state explaining that,
  // so the page doesn't look broken.
  const { data: locationsData } = useLocations();
  const selectedIsWarehouse = useMemo(() => {
    if (!selectedLocationId) return false;
    const loc = (locationsData?.data as any[] | undefined)?.find((l) => l.id === selectedLocationId);
    return isWarehouseType(loc?.type);
  }, [locationsData, selectedLocationId]);
  // Resolve scope IDs → human-readable names for the scope cell.
  const { data: deptData } = useDepartments();
  const { data: catData } = useCategories();
  const { data: productsData } = useProducts({ pageSize: 500 });

  const all = useMemo(() => {
    const raw = data?.data ?? [];
    if (selectedIsWarehouse) return [];
    const locationScoped = !selectedLocationId
      ? raw
      : raw.filter((p) => !p.locationIds || p.locationIds.length === 0 || p.locationIds.includes(selectedLocationId));
    if (statusFilter === 'all') return locationScoped;
    const now = Date.now();
    // Running / Scheduled / Expired describe the window of an active promotion;
    // a deactivated one is only ever "deactivated", whatever its dates.
    return locationScoped.filter((p) => {
      if (statusFilter === 'deactivated') return !p.isActive;
      if (!p.isActive) return false;
      if (statusFilter === 'running') return isRunning(p, now);
      if (statusFilter === 'scheduled') return isScheduled(p, now);
      if (statusFilter === 'expired') return isExpired(p, now);
      return true;
    });
  }, [data, selectedIsWarehouse, selectedLocationId, statusFilter]);

  // Memoize the name lookup maps so their reference is stable across renders
  // when the source data hasn't changed. Without this, the columns useMemo
  // deps churn every render → cells re-render every paint → useReactTable
  // re-derives → render storm.
  const locName = useMemo(() => buildNameMap(locationsData?.data as any[] | undefined), [locationsData]);
  const deptName = useMemo(() => buildNameMap(deptData?.data as any[] | undefined), [deptData]);
  const catName = useMemo(() => buildNameMap(catData?.data as any[] | undefined), [catData]);
  const prodName = useMemo(() => buildNameMap(productsData?.data as any[] | undefined), [productsData]);

  const createMut = useCreatePromotion();
  const updateMut = useUpdatePromotion();
  const deleteMut = useDeletePromotion();
  const activeMut = useSetPromotionActive();
  const permissions = usePermissions();
  const canCreate = permissions.has('promotions.create');
  // Edit covers starting and stopping.
  const canEdit = permissions.has('promotions.edit');
  const canDelete = permissions.has('promotions.delete');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const sorted = useMemo(
    // Newest start first; open-ended (null) starts coalesce to epoch 0 so they
    // sort to the bottom rather than throwing on new Date(null).
    () => [...all].sort((a, b) => new Date(b.startsAt ?? 0).getTime() - new Date(a.startsAt ?? 0).getTime()),
    [all],
  );
  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const paginated = useMemo(() => sorted.slice((page - 1) * perPage, page * perPage), [sorted, page, perPage]);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [duplicating, setDuplicating] = useState<Promotion | null>(null);
  const [stopTarget, setStopTarget] = useState<Promotion | null>(null);
  const [startTarget, setStartTarget] = useState<Promotion | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Promotion | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDuplicating(null);
    setShowForm(true);
  };

  const openEdit = (p: Promotion) => {
    setEditing(p);
    setDuplicating(null);
    setShowForm(true);
  };

  const openDuplicate = (p: Promotion) => {
    setEditing(null);
    setDuplicating(p);
    setShowForm(true);
  };

  const columns: ColumnDef<Promotion>[] = useMemo(
    () => [
      {
        id: 'expander',
        header: () => null,
        size: 36,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            aria-label={row.getIsExpanded() ? 'Collapse scope' : 'Expand scope'}
            className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
          >
            {row.getIsExpanded() ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className="font-medium text-foreground">{row.original.name}</span>,
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => <span className="whitespace-nowrap">{TYPE_LABEL[row.original.type]}</span>,
      },
      {
        id: 'window',
        header: 'Window',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {row.original.startsAt ? formatDateTime(row.original.startsAt) : 'Always'} →{' '}
            {row.original.endsAt ? formatDateTime(row.original.endsAt) : 'No end'}
          </span>
        ),
      },
      {
        id: 'scope',
        header: 'Scope',
        cell: ({ row }) => <ScopePreview promo={row.original} locName={locName} />,
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => {
          const status = statusLabel(row.original);
          return (
            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${status.tone}`}>
              {status.label}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const p = row.original;
          const now = Date.now();
          const deactivated = !p.isActive;
          const expired = p.isActive && isExpired(p, now);
          const running = p.isActive && isRunning(p, now);
          const scheduled = p.isActive && isScheduled(p, now);
          // Edit hidden on Expired (per requirement). Running is editable but
          // the form locks all fields except End at. A deactivated promotion is
          // fully editable — stopping is the designed way to make structural
          // changes (the API's running-promotion guard says so).
          const showEdit = canEdit && (running || scheduled || deactivated);
          // Stop applies while the promotion can still fire (Running + Scheduled).
          const showStop = canEdit && (running || scheduled);
          // Start brings a deactivated promotion back, provided its window
          // hasn't already closed — for those, edit the end date first.
          const showStart = canEdit && deactivated && !isExpired(p, now);
          // Delete on Expired and Deactivated — soft-deletes via DELETE, which
          // removes the row from every list for good.
          const showDelete = canDelete && (expired || deactivated);
          return (
            <div className="flex items-center gap-1">
              {showEdit && (
                <button
                  type="button"
                  onClick={() => openEdit(p)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
                  title="Edit"
                  aria-label="Edit promotion"
                >
                  <Pencil className="h-4 w-4 text-primary" />
                </button>
              )}
              {canCreate && (
                <button
                  type="button"
                  onClick={() => openDuplicate(p)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
                  title="Duplicate"
                  aria-label="Duplicate promotion"
                >
                  <Copy className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
              {showStop && (
                <button
                  type="button"
                  onClick={() => setStopTarget(p)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-orange-50"
                  title="Stop"
                  aria-label="Stop promotion"
                >
                  <Ban className="h-4 w-4 text-orange-500" />
                </button>
              )}
              {showStart && (
                <button
                  type="button"
                  onClick={() => setStartTarget(p)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-green-50"
                  title="Start"
                  aria-label="Start promotion"
                >
                  <Play className="h-4 w-4 text-green-600" />
                </button>
              )}
              {showDelete && (
                <button
                  type="button"
                  onClick={() => setDeleteTarget(p)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-red-50"
                  title="Delete"
                  aria-label="Delete promotion"
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [locName, deptName, catName, prodName, canCreate, canEdit, canDelete],
  );

  const [expanded, setExpanded] = useState<ExpandedState>({});
  const table = useReactTable({
    data: paginated,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    // Stable row IDs so expansion state survives data refresh (e.g. after
    // editing a promotion the same row stays open).
    getRowId: (row) => row.id,
  });

  const handleSubmit = async (input: PromotionInput) => {
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, ...input });
      toast.success(`Updated promotion "${input.name}"`);
    } else {
      await createMut.mutateAsync(input);
      toast.success(`Created promotion "${input.name}"`);
    }
  };

  const handleConfirmStop = async () => {
    if (!stopTarget) return;
    try {
      await activeMut.mutateAsync({ id: stopTarget.id, isActive: false });
      toast.success(`Stopped "${stopTarget.name}"`);
      setStopTarget(null);
    } catch (e: any) {
      toast.error(e?.error?.message || 'Failed to stop promotion');
    }
  };

  const handleConfirmStart = async () => {
    if (!startTarget) return;
    try {
      await activeMut.mutateAsync({ id: startTarget.id, isActive: true });
      toast.success(`Started "${startTarget.name}"`);
      setStartTarget(null);
    } catch (e: any) {
      toast.error(e?.error?.message || 'Failed to start promotion');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
    } catch (e: any) {
      toast.error(e?.error?.message || 'Failed to delete promotion');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setDuplicating(null);
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Promotions" description="Product-level promotions applied at POS checkout">
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter);
              setPage(1);
            }}
            aria-label="Filter promotions by status"
            className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
          >
            <option value="all">All statuses</option>
            <option value="running">Running</option>
            <option value="scheduled">Scheduled</option>
            <option value="expired">Expired</option>
            <option value="deactivated">Deactivated</option>
          </select>
          {!selectedIsWarehouse && canCreate && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" /> New Promotion
            </Button>
          )}
        </div>
      </PageHeader>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : total === 0 ? (
        <div className="rounded-lg border border-border bg-white p-8 text-center text-sm text-muted-foreground">
          {selectedIsWarehouse ? (
            <>
              Promotions don't run at warehouses — they only apply at store locations. Switch the location in the header
              to a store to manage its promotions.
            </>
          ) : statusFilter !== 'all' ? (
            <EmptyState
              icon={SearchX}
              title={`No ${statusFilter} promotions`}
              description="Try a different status filter."
              className="py-4"
            />
          ) : (
            <EmptyState
              icon={BadgePercent}
              title="No promotions yet"
              description={
                <>
                  Click <strong>New Promotion</strong> to create one.
                </>
              }
              className="py-4"
            />
          )}
        </div>
      ) : (
        <>
          <DataTable
            table={table}
            columns={columns}
            renderSubRow={(row) => (
              <ScopeSummary
                promo={row.original}
                locName={locName}
                deptName={deptName}
                catName={catName}
                prodName={prodName}
              />
            )}
          />
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
            perPageOptions={PER_PAGE_OPTIONS}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        </>
      )}

      <PromotionForm
        open={showForm}
        onClose={closeForm}
        initial={editing}
        duplicateFrom={duplicating}
        onSubmit={handleSubmit}
        saving={createMut.isPending || updateMut.isPending}
      />

      <ConfirmDialog
        open={!!stopTarget}
        onCancel={() => setStopTarget(null)}
        onConfirm={handleConfirmStop}
        title="Stop this promotion?"
        message={
          stopTarget
            ? `"${stopTarget.name}" will stop applying at POS checkout immediately. It stays in the list as Deactivated and can be started again at any time.`
            : ''
        }
        confirmLabel="Stop promotion"
        variant="warning"
        loading={activeMut.isPending}
      />

      <ConfirmDialog
        open={!!startTarget}
        onCancel={() => setStartTarget(null)}
        onConfirm={handleConfirmStart}
        title="Start this promotion again?"
        message={
          startTarget
            ? `"${startTarget.name}" will apply at POS checkout again${
                isScheduled(startTarget, Date.now()) ? ' once its start date arrives' : ' immediately'
              }.`
            : ''
        }
        confirmLabel="Start promotion"
        variant="warning"
        loading={activeMut.isPending}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Delete this promotion?"
        message={
          deleteTarget
            ? `Delete "${deleteTarget.name}" from the list? Past transactions still reference it for accounting purposes.`
            : ''
        }
        confirmLabel="Delete promotion"
        variant="danger"
        loading={deleteMut.isPending}
      />
    </div>
  );
}

// ── Scope preview (collapsed row) ────────────────────────────────────────────
// One-line summary shown in the table cell. Cashiers expand the row for the
// full breakdown (departments / categories / products / free items).
function ScopePreview({ promo, locName }: { promo: Promotion; locName: Map<string, string> }) {
  const locIds = promo.locationIds ?? [];
  const locText =
    locIds.length === 0
      ? 'All locations'
      : locIds
          .slice(0, 2)
          .map((id) => locName.get(id) ?? id.slice(0, 8))
          .join(', ') + (locIds.length > 2 ? ` (+${locIds.length - 2} more)` : '');

  const scopeCounts: string[] = [];
  const deptCount = (promo.departments ?? []).length;
  const catCount = (promo.categories ?? []).length;
  const prodCount = (promo.products ?? []).length;
  if (deptCount) scopeCounts.push(`${deptCount} department${deptCount === 1 ? '' : 's'}`);
  if (catCount) scopeCounts.push(`${catCount} categor${catCount === 1 ? 'y' : 'ies'}`);
  if (prodCount) scopeCounts.push(`${prodCount} product${prodCount === 1 ? '' : 's'}`);
  const productsText = scopeCounts.length === 0 ? 'All products' : scopeCounts.join(' · ');

  return (
    <div className="text-xs text-muted-foreground space-y-0.5">
      <div className="truncate">
        <span className="font-medium text-foreground">Locations:</span> {locText}
      </div>
      <div className="truncate">
        <span className="font-medium text-foreground">Products:</span> {productsText}
      </div>
    </div>
  );
}

// ── Scope summary ────────────────────────────────────────────────────────────
// Compact, human-readable rundown of what a promotion applies to. Empty
// scope (no locations/depts/cats/products selected) prints "All products /
// all locations" so the row never looks ambiguous.
function ScopeSummary({
  promo,
  locName,
  deptName,
  catName,
  prodName,
}: {
  promo: Promotion;
  locName: Map<string, string>;
  deptName: Map<string, string>;
  catName: Map<string, string>;
  prodName: Map<string, string>;
}) {
  const locIds = promo.locationIds ?? [];
  const depts = promo.departments ?? [];
  const cats = promo.categories ?? [];
  const prods = promo.products ?? [];

  const incDepts = depts.filter((d) => d.mode === 'include');
  const excDepts = depts.filter((d) => d.mode === 'exclude');
  const incCats = cats.filter((c) => c.mode === 'include');
  const excCats = cats.filter((c) => c.mode === 'exclude');
  const applyProds = prods.filter((p) => p.role === 'applies_to' || p.role === 'trigger');
  const rewardProds = prods.filter((p) => p.role === 'reward');

  const lines: { label: string; value: string }[] = [];

  lines.push({
    label: 'Locations',
    value: locIds.length === 0 ? 'All locations' : locIds.map((id) => locName.get(id) ?? id.slice(0, 8)).join(', '),
  });
  if (incDepts.length || excDepts.length) {
    const parts: string[] = [];
    if (incDepts.length) {
      parts.push(`Include: ${incDepts.map((d) => deptName.get(d.id) ?? d.id.slice(0, 8)).join(', ')}`);
    }
    if (excDepts.length) {
      parts.push(`Exclude: ${excDepts.map((d) => deptName.get(d.id) ?? d.id.slice(0, 8)).join(', ')}`);
    }
    lines.push({ label: 'Departments', value: parts.join(' · ') });
  }
  if (incCats.length || excCats.length) {
    const parts: string[] = [];
    if (incCats.length) {
      parts.push(`Include: ${incCats.map((c) => catName.get(c.id) ?? c.id.slice(0, 8)).join(', ')}`);
    }
    if (excCats.length) {
      parts.push(`Exclude: ${excCats.map((c) => catName.get(c.id) ?? c.id.slice(0, 8)).join(', ')}`);
    }
    lines.push({ label: 'Categories', value: parts.join(' · ') });
  }
  // Prefer the name hydrated by the products API (works for any catalog size);
  // fall back to the preloaded map, then a short id.
  const nameOf = (p: { id: string; name?: string | null }) => p.name || prodName.get(p.id) || p.id.slice(0, 8);
  // For discounted_price, surface per-product override prices so a merchant can
  // tell which products carry a different promo price from the default.
  const priceOverrides = new Map<string, number>(
    promo.type === 'discounted_price'
      ? ((promo.config?.productPrices as Array<{ productId: string; price: number }> | undefined) ?? []).map((o) => [
          o.productId,
          o.price,
        ])
      : [],
  );
  // Default promo unit price for discounted_price — shown for products without
  // their own override so every product surfaces its effective price.
  const defaultPrice =
    promo.type === 'discounted_price' && Number.isFinite(Number(promo.config?.price))
      ? Number(promo.config.price)
      : undefined;
  const labelWithPrice = (p: { id: string; name?: string | null }) => {
    const price = priceOverrides.get(p.id) ?? defaultPrice;
    return price != null ? `${nameOf(p)} (${formatCurrency(price)})` : nameOf(p);
  };
  if (applyProds.length) {
    lines.push({
      label: 'Products',
      value: applyProds.map(labelWithPrice).join(', '),
    });
  }
  if (rewardProds.length) {
    lines.push({
      label: 'Free items',
      value: rewardProds.map(nameOf).join(', '),
    });
  }
  if (incDepts.length + incCats.length + applyProds.length === 0) {
    lines.push({ label: 'Products', value: 'All products' });
  }

  return (
    <div className="space-y-1 text-sm">
      {lines.map((l) => (
        <div key={l.label} className="break-words">
          <span className="font-medium text-foreground">{l.label}:</span>{' '}
          <span className="text-muted-foreground">{l.value}</span>
        </div>
      ))}
    </div>
  );
}
